import { defineStore } from 'pinia'
import { computed, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

export const useStore = defineStore('store', () => {
    const quoteBuilder = ref()
    const cars = ref()
    const teamMembers = ref()
    const salary = ref(0)
    const budget = ref()
    const weeklyBudget = ref()
    const kmsPerYear = ref()
    const leaseLength = ref()
    const selectedCar = ref(null)
    const selectedMake = ref('All')
    const selectedQuote = ref(null)
    const postcode = ref(null)
    const quotes = ref({})

    const lowestCarPrice = computed(
        () =>
            cars.value.reduce((currentLowest, car) => {
                if (
                    car.price.from > 0 &&
                    (currentLowest == undefined ||
                        car.price.from < currentLowest)
                ) {
                    return car.price.from
                }

                return currentLowest
            }, undefined) ?? 0,
    )

    const highestCarPrice = computed(
        () =>
            cars.value.reduce((currentHighest, car) => {
                const carPrice = car.price.to || car.price.from

                if (currentHighest == undefined || carPrice > currentHighest) {
                    return carPrice
                }

                return currentHighest
            }, undefined) ?? 0,
    )

    const availableCars = computed(() => {
        if (!weeklyBudget.value) {
            return []
        }

        // Calculate the min & max amounts that can be paid off in 5 years
        const maxTermInYears = 5
        const weeksInYear = 52
        const minAmount = weeklyBudget.value[0] * weeksInYear * maxTermInYears
        const maxAmount = weeklyBudget.value[1] * weeksInYear * maxTermInYears

        // Filter the cars based on the calculated amounts
        const affordableCars = cars.value.filter((car) => {
            const lowestPrice = car.price.from
            const highestPrice = car.price.to || lowestPrice

            return (
                (lowestPrice >= minAmount && lowestPrice <= maxAmount) ||
                (highestPrice >= minAmount && highestPrice <= maxAmount)
            )
        })

        // Filter the cars based on the selected make
        if (selectedMake.value && selectedMake.value !== 'All') {
            return affordableCars.filter(
                (car) =>
                    car.make?.title.toUpperCase() ===
                    selectedMake.value.toUpperCase(),
            )
        }

        // Return the filtered list of cars
        return affordableCars;
    })

    const carMakes = computed(() => {
        if (cars.value.length === 0) {
            console.log('no cars, no makes')
            return []
        }

        // Return unique car makes in alphabetical order
        return [...new Set(cars.value.map((car) => car.make?.title || ''))]
            .filter((make) => !!make)
            .sort()
    })

    const state = () => {
        if (!postcode.value) {
            return null
        }

        postcode.value = parseInt(postcode.value)

        for (const [state, ranges] of Object.entries(statePostcodeRanges())) {
            for (const range of ranges) {
                if (
                    postcode.value >= range.min &&
                    postcode.value <= range.max
                ) {
                    return state
                }
            }
        }
    }

    const teamMembersInState = computed(() =>
        teamMembers.value.filter(
            (member) =>
                member.locations.filter((location) => location.title == state())
                    .length > 0,
        ),
    )

    const isCarInBudget = (car) => {
        if (!car) {
            return false
        }

        const carHighest = car.price.to || car.price.from

        return (
            carHighest >= budget.value[0] && car.price.from <= budget.value[1]
        )
    }

    const isSalaryWithinLimits = () => {
        return (
            salary.value >= quoteBuilder.value.salary_amounts.lower_limit &&
            salary.value <= quoteBuilder.value.salary_amounts.upper_limit
        )
    }

    const getSimilarlyPricedCars = (numberOfCarsToGet = 4) => {
        if (!selectedCar.value) {
            return null
        }

        const carsSortedByPrice = availableCars.value.toSorted((a, b) => {
            return Math.sign(a.price.from - b.price.from)
        })

        const selectedCarIndex = carsSortedByPrice.findIndex(
            (car) => car.title == selectedCar.value.title,
        )

        carsSortedByPrice.splice(selectedCarIndex, 1)

        const offset = Math.floor(numberOfCarsToGet / 2)
        const indexFrom = Math.max(selectedCarIndex - offset, 0)
        const indexTo = indexFrom + numberOfCarsToGet

        return carsSortedByPrice.slice(indexFrom, indexTo)
    }

    const getLowestPricedVariant = (car) => {
        if (!car || !car.variants || car.variants.length === 0) {
            return null
        }

        return car.variants.reduce((lowest, variant) => {
            if (!lowest || variant.price < lowest.price) {
                return variant
            }

            return lowest
        }, null)
    }

    const getQuotesForSelectedCar = (bypassCache = false) => {
        if (!selectedCar.value) {
            console.warn('No car selected')
            return null
        }

        if (!isSalaryWithinLimits()) {
            console.warn('Salary out of range')
            return null
        }

        const variant = getLowestPricedVariant(selectedCar.value)

        const values = {
            variantId: variant.id,
            variantCode: variant.code,
            variantPrice: variant.price,
            salary: parseInt(salary.value),
            kmsPerYear: parseInt(kmsPerYear.value),
        }

        quotes.value[variant.id] = quotes.value[variant.id] || {}

        quotes.value[variant.id][values.salary] =
            quotes.value[variant.id][values.salary] || {}

        quotes.value[variant.id][values.salary][values.kmsPerYear] =
            quotes.value[variant.id][values.salary][values.kmsPerYear] || {}

        const quoteRequests = []

        for (
            let leaseLengthYears = quoteBuilder.value.lease_length.lower_limit;
            leaseLengthYears <= quoteBuilder.value.lease_length.upper_limit;
            leaseLengthYears++
        ) {
            if(!bypassCache) {
                if (
                    quotes.value[variant.id][values.salary][
                        values.kmsPerYear
                    ].hasOwnProperty(leaseLengthYears) &&
                    !quotes.value[variant.id][values.salary][values.kmsPerYear][
                        leaseLengthYears
                    ].hasOwnProperty('error')
                ) {
                    // we already have a quote for this lease length
                    continue
                }
            }

            quotes.value[variant.id][values.salary][values.kmsPerYear][
                leaseLengthYears
            ] = {}

            // Add the quote request to the list of requests.
            quoteRequests.push(
                useStatamicPost()
                    .submit('quote', {
                        ...values,
                        leaseLength: leaseLengthYears,
                    })
                    .then((response) => {
                        quotes.value[variant.id][values.salary][
                            values.kmsPerYear
                        ][leaseLengthYears] = response.quote
                    })
                    .catch((error) => {
                        console.error(
                            `Unable to fetch quote #${leaseLengthYears}:`,
                            error,
                        )

                        quotes.value[variant.id][values.salary][
                            values.kmsPerYear
                        ][leaseLengthYears].error = error
                    }),
            )
        }

        if (quoteRequests.length > 0) {
            // If the quote requests are not already cached, wait for them to complete.
            Promise.all(quoteRequests).then(() => {
                selectedQuote.value =
                    quotes.value[variant.id][values.salary][values.kmsPerYear][
                        leaseLength.value
                    ]
            })
        } else {
            // If the quote requests are already cached, set the selected quote immediately.
            selectedQuote.value =
                quotes.value[variant.id][values.salary][values.kmsPerYear][
                    leaseLength.value
                ]
        }
    }

    const selectCar = (car) => {
        selectedCar.value = car
        selectedQuote.value = null
        getQuotesForSelectedCar()
    }

    const clearCar = () => {
        selectedCar.value = null
        selectedQuote.value = null
    }

    const clearQuotes = () => {
        quotes.value = {}
        selectedQuote.value = null
    }

    const updateSelectedQuote = (bypassCache = false) => {
        selectedQuote.value = null
        getQuotesForSelectedCar(bypassCache)
    }

    const haveQuotesForSelectedCar = () => {
        if (!selectedCar.value) {
            return false
        }

        const lowestPricedVariantId = getLowestPricedVariant(
            selectedCar.value,
        )?.id

        if (!lowestPricedVariantId) {
            return false
        }

        return quotes.value.hasOwnProperty(lowestPricedVariantId)
    }

    // watch budget min/max for changes
    watch(budget, () => {
        // deselect selected car if it falls outside the budget
        if (!isCarInBudget(selectedCar.value)) {
            clearCar()
        }
    })

    const updateKmsPerYear = useDebounceFn(() => {
        updateSelectedQuote()
    }, 600)

    const updateSalary = useDebounceFn(() => {
        updateSelectedQuote()
    }, 750)

    const updateLeaseLength = useDebounceFn(() => {
        updateSelectedQuote()
    }, 100)

    watch(kmsPerYear, updateKmsPerYear)
    watch(salary, updateSalary)
    watch(leaseLength, updateLeaseLength)

    return {
        quoteBuilder,
        teamMembers,
        cars,
        salary,
        budget,
        weeklyBudget,
        kmsPerYear,
        leaseLength,
        selectedCar,
        selectedMake,
        selectedQuote,
        postcode,
        quotes,
        lowestCarPrice,
        highestCarPrice,
        availableCars,
        carMakes,
        state,
        teamMembersInState,
        getSimilarlyPricedCars,
        getQuotesForSelectedCar,
        selectCar,
        clearCar,
        haveQuotesForSelectedCar,
        updateSelectedQuote,
    }
})
